import { PageDivider } from '@src/shared/components/PageDivider';
import React, { FC, useState } from 'react';
import CourierDetailActionsButton from '../CourierDetailActionsButton';
import { EBottomSheetAction, TBottomSheetAction } from '../CourierBottomSheet';
import { TCourier, COURIER_STATUS } from '../../services/api';
import { EBadgeStatus, JetStatusBadge } from '@src/shared/components/JetStatusBadges';
import { useTranslation } from 'react-i18next';
import { useReinviteCourierMutation } from '../../queries';
import { trackInvitationSendInviteAgainSuccess } from '../../tracking/invitation';
import { useQueryClient } from 'react-query';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { encryptHash } from '@src/shared/utils';
import { NotificationCategories, useNotificationsActions } from '@src/shared/context/notifications';
import styles from './CourierDetailActionsResend.module.scss';
import PIEText from '@src/shared/components/PIEText';
import { IconSend, IconUserCancelled } from '@justeattakeaway/pie-icons-react';
interface ICourierDetailActionsResendProps extends TCourier {
  onAction: (action: TBottomSheetAction) => void;
  onDismiss: () => void;
}

export const CourierDetailActionsResend: FC<ICourierDetailActionsResendProps> = ({
  onAction,
  onDismiss,
  status,
  email,
  hash,
}) => {
  /* ReactQuery */
  const queryClient = useQueryClient();

  /* Context State */
  const { restaurant } = useKeycloakUserState();

  /* Hooks */
  const { t } = useTranslation();
  const [isPromising, setIsPromising] = useState<boolean>();
  const { notifyInformation, notifyError } = useNotificationsActions(
    NotificationCategories.CourierManagement
  );

  const reinviteCourierMutation = useReinviteCourierMutation({
    onSuccess: (data: TCourier) => {
      trackInvitationSendInviteAgainSuccess(
        restaurant?.language,
        restaurant?.country,
        encryptHash(data.email)
      );
      queryClient.invalidateQueries('couriers');
    },
    onError: () => {
      notifyError(t('manage_couriers.commonError'), '');
    },
    onSettled: () => {
      onDismiss();
      setIsPromising(false);
      notifyInformation(t('manage_couriers.COURIER_INVITE_SUCCESS_MESSAGE'), '');
    },
  });

  /* React Query Handlers */
  const onLinkInvitationResend = async ({ email }: { email: string }) => {
    setIsPromising(true);
    reinviteCourierMutation.mutate(email);
  };

  /* Component Actions Handler */
  const handleOnButtonClick = (e: TBottomSheetAction) => {
    if (e.type === EBottomSheetAction.ReSend) {
      onLinkInvitationResend({ email });
    }

    if (e.type === EBottomSheetAction.Delete) {
      onAction({ type: e.type, source: { hash, text: email } });
    }
  };

  return (
    <div className={styles.courierDetailActionsResendContainer}>
      <div className={styles.courierDetialActionsResendContent}>
        <PIEText
          text={email}
          size={'var(--xds-font-size-16)'}
          fontWeight="var(--xds-font-weight-regular)"
        />
        <span>
          <JetStatusBadge
            status={
              status === COURIER_STATUS.DISCONNECTED
                ? EBadgeStatus.DISCONNECT
                : EBadgeStatus.INVITATION
            }
            badgeText={
              status === COURIER_STATUS.DISCONNECTED
                ? t('manage_couriers.DISCONNECTED')
                : t('manage_couriers.REQUEST_SENT')
            }
            textWeigth="regular"
            iconComponent={
              status === COURIER_STATUS.DISCONNECTED ? (
                <IconUserCancelled size="xs" />
              ) : (
                <IconSend size="xs" />
              )
            }
          ></JetStatusBadge>
        </span>
        <CourierDetailActionsButton
          buttonText={t('manage_couriers.RESEND_BUTTON')}
          onButtonAction={handleOnButtonClick}
          actionType={EBottomSheetAction.ReSend}
          clearContainerSpaces={true}
          isLoading={isPromising}
          startIcon={<IconSend size="m" />}
          customStyle={{
            marginTop: 'var(--xds-spacing-d)',
            height: '48px',
            width: '100%',
            padding: `0 var(--xds-spacing-e)`,
          }}
        />
      </div>
      <PageDivider
        styles={{
          backgroundColor: '#00000014',
          height: '12px',
          flexShrink: '0',
          width: '100%',
        }}
      />
      <CourierDetailActionsButton
        buttonText={t('manage_couriers.REMOVE_BUTTON')}
        onButtonAction={handleOnButtonClick}
        actionType={EBottomSheetAction.Delete}
        containerCustomStyle={{ width: '100%' }}
      />
    </div>
  );
};
